import React from 'react';
import Select, { ArrowRendererHandler, ArrowRendererProps, MenuRendererHandler, MenuRendererProps, Option, ValueRendererHandler } from 'react-select';
import { color_colors_ocean, color_shades_dark, color_shades_darkest, color_variants_ocean_xlight } from '../../constants/colors';
import styled from '@emotion/styled';
import ToggleIndicator from '../general/ToggleIndicator';
import { style_border_default } from '../../constants/stylesValues';

export type Size = "small" | "medium"

interface ImageOption extends Option {
    image?: string
    selectedImage?: string
    description?: string
}
interface SelectProps {
    name?: string
    type?: string
    placeholder?: string
    value?: string | ImageOption | null
    disabled?: boolean
    onChange?: (selectedOption: ImageOption | null) => void
    onBlur?: (e: any) => void
    valueRenderer?: ValueRendererHandler<any>
    menuRenderer?: MenuRendererHandler<any>
    arrowRenderer?: ArrowRendererHandler
    label?: string
    id?: string
    options: ImageOption[]
    useFormTheme?: boolean
    hidden?: boolean
    labelIcon?: string
    clearable?: boolean
    searchable?: boolean
    clearValueText?: string
    className?: string
    useForm?: boolean
    optionImageStyle?: string
    size?: Size
}

const getSelectField = (props: SelectProps, menuProps: MenuRendererProps) => {
    return (
        <>
            <Triangle />
            {props.options.map((option, index) =>
                <div key={index}>
                    <CustomSelectOption className="user-entry-option" onClick={() => menuProps.selectValue(option)} >
                        {option.image ? <img className={props.optionImageStyle ?? ''} src={option.image} /> : null}
                        <div className={menuProps.valueArray && menuProps.valueArray.length > 0 && menuProps.valueArray[0].value == option.value ? "option-selected" : undefined} onMouseOver={() => menuProps.focusOption(option)}>{option.label}</div>
                    </CustomSelectOption>
                    {index != menuProps.options.length - 1 ? <Divider /> : null}
                </div>
            )}
        </>
    );
}

const DropDownSelectField = (props: SelectProps) => {
    // Convert string value to option object for react-select
    const getValueForSelect = () => {
        if (!props.value) return null;

        // If value is already an option object, use it
        if (typeof props.value === 'object' && props.value !== null) {
            return props.value;
        }

        // If value is a string, find the matching option
        const matchingOption = props.options.find(option => option.value === props.value);
        return matchingOption || null;
    };

    // Handle onChange to call props.onChange with the selected option
    const handleChange = (selectedOption: ImageOption | null) => {
        if (props.onChange) {
            props.onChange(selectedOption);
        }
    };

    const selectProps = {
        ...props,
        value: getValueForSelect(),
        onChange: handleChange
    };

    // Use default menu renderer when searchable is true to preserve search functionality
    // Custom menu renderer overrides react-select's built-in search functionality
    const shouldUseCustomMenu = !props.searchable && !props.menuRenderer;
    const menuRenderer = shouldUseCustomMenu
        ? (menuProps: MenuRendererProps) => (
            <MenuOptionsWrapper>
                {getSelectField(props, menuProps)}
            </MenuOptionsWrapper>
        )
        : props.menuRenderer;

    return (
        <>
            {props.size === "medium" ?
                <CustomSelectMedium
                    {...selectProps}
                    arrowRenderer={props.arrowRenderer ? props.arrowRenderer : (p: ArrowRendererProps) => <Arrow direction={p.isOpen ? 'up' : 'down'} />}
                    menuRenderer={menuRenderer}
                />
                :
                props.size === "small" ?
                    <CustomSelectSmall
                        {...selectProps}
                        arrowRenderer={props.arrowRenderer ? props.arrowRenderer : (p: ArrowRendererProps) => <Arrow direction={p.isOpen ? 'up' : 'down'} />}
                        menuRenderer={menuRenderer}
                    />
                    :
                    <CustomSelect
                        {...selectProps}
                        arrowRenderer={props.arrowRenderer ? props.arrowRenderer : (p: ArrowRendererProps) => <Arrow direction={p.isOpen ? 'up' : 'down'} />}
                        menuRenderer={menuRenderer}
                    />
            }
        </>
    );
}

const Arrow = styled(ToggleIndicator)`
    height: 6px;
    width: 6px;
`;

const Divider = styled.div`
    display: block;
    height: 1px;
    border: 0;
    border-top: 1px solid ${color_shades_dark};
    padding: 0;
`;

const CustomSelectOption = styled.div`
    display: flex;
    flex-direction: row;
    line-height: 36px;
    padding-left: 15px;
    &.user-entry-option:hover{
        background-color: ${color_variants_ocean_xlight};
        cursor: pointer;
    }
    div{
        margin-left:10px; 
        color: ${color_shades_darkest};
        &.option-selected{
            color: ${color_colors_ocean};
        }
    }
`;

const MenuOptionsWrapper = styled.div`
    margin-top: 8px;
`;

const Triangle = styled.div`
    transform: rotate(45deg);
    width: 8px;
    height: 8px;
    border-top: solid 1px ${color_shades_dark};
    border-left: solid 1px ${color_shades_dark};
    background: white;
    margin-top: -5px;
    margin-left: 112px;
    position: absolute;
`;

const CustomSelect = styled(Select)`
    .Select-control {
        border-radius: 20px;
    }
    .Select-menu{
        margin-top:-8px;
    }
    .Select-menu-outer{
        margin-top: 21px;
        border-radius: 6px;
        border-top-color: ${color_shades_dark};
    }
    .Select-value:hover{
        cursor: pointer;
    }
    .Select-input {
        padding: 8px 12px;
        font-size: 14px;
    }
    .Select-option {
        padding: 8px 15px;
        cursor: pointer;
        &:hover {
            background-color: ${color_variants_ocean_xlight};
        }
        &.is-focused {
            background-color: ${color_variants_ocean_xlight};
        }
    }
    &.is-open  {
        .Select-control {
            border-bottom-right-radius: 20px;
            border-bottom-left-radius: 20px;
            border-color: ${color_shades_dark};
        }
    }
`;

const CustomSelectSmall = styled(Select)`
    flex-grow: 1;
    .Select-placeholder {
        padding: 10px;
        display: flex;
        align-items: center;
    }
    .Select-control {
        height: 32px;
        flex-grow: 1;
        border: ${style_border_default};
        box-shadow: 0px 1px 2px rgba(50, 70, 97, 0.1);
        border-radius: 4px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        cursor: pointer;
        font-size: 12px;
        font-weight: 400;
    }
    .Select-control .Select-multi-value-wrapper .Select-value {
        display: flex;
        align-items: center;
        margin-left: 8px;
    }
    .Select-menu {
        margin-top: -8px;
    }
    .Select-menu-outer {
        margin-top: 12px;
        border-radius: 4px;
        border: ${style_border_default};
    }
    .Select-input {
        padding: 4px 8px;
        font-size: 12px;
    }
    .Select-option {
        padding: 6px 12px;
        cursor: pointer;
        font-size: 12px;
        &:hover {
            background-color: ${color_variants_ocean_xlight};
        }
        &.is-focused {
            background-color: ${color_variants_ocean_xlight};
        }
    }
    &.is-open  {
        .Select-control {
            border-bottom-right-radius: 4px;
            border-bottom-left-radius: 4px;
            border: ${style_border_default};
        }
    }
`;

const CustomSelectMedium = styled(Select)`
    flex-grow: 1;
    .Select-placeholder {
        padding: 16px;
        display: flex;
        align-items: center;
    }
    .Select-control {
        height: 48px;
        flex-grow: 1;
        border: ${style_border_default};
        box-shadow: 0px 1px 2px rgba(50, 70, 97, 0.1);
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        cursor: pointer;
        font-size: 14px;
        font-weight: 400;
    }
    .Select-control .Select-multi-value-wrapper .Select-value {
        display: flex;
        align-items: center;
        margin-left: 8px;
    }
    .Select-menu {
        margin-top: -8px;
        margin-bottom: -8px;
        padding-bottom: 8px;
    }
    .Select-menu-outer {
        margin-top: 12px;
        border-radius: 8px;
        border: ${style_border_default};
    }
    .Select-input {
        padding: 8px 12px;
        font-size: 14px;
    }
    .Select-option {
        padding: 8px 15px;
        cursor: pointer;
        &:hover {
            background-color: ${color_variants_ocean_xlight};
        }
        &.is-focused {
            background-color: ${color_variants_ocean_xlight};
        }
    }
    &.is-open  {
        .Select-control {
            border-bottom-right-radius: 8px;
            border-bottom-left-radius: 8px;
            border: ${style_border_default};
        }
    }
`;

export default DropDownSelectField;